import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";

import { colType, TableProps } from "../../Types";

import { FixedSizeList as List, ListChildComponentProps } from "react-window";
import DSDialog from "../../views/system-design/DSDialog";
import { InputAdornment, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DSBox from "../../views/system-design/DSBox";
import DSCard from "../../views/system-design/DSCard";
import DSTypography from "../../views/system-design/DSTypography";
import DSTextField from "../../views/system-design/DSTextField";
import DSButton from "../../views/system-design/DSButton";
import DSPrimaryButton from "../../views/system-design/DSPrimaryButton";
import DSSecondaryButton from "../../views/system-design/DSSecondaryButton";
import { APPBAR_HEIGHT } from "consts/layout";
import DSCheckBox from "../../views/system-design/DSCheckBox";
import DSFormControlLabel from "../../views/system-design/DSFormControlLabel";
import DSCircularProgress from "../../views/system-design/DSCircularProgress";
let timeout: any = -1;

type RowData = {
  dataState: TableProps["data"];
  cols: TableProps["cols"];
  colWidth: number;
  hasActions: boolean;
  rowBuild: TableProps["rowBuild"];
  hasModal: boolean;
  tableId: string;
  selected: TableProps["data"];
  showActions: boolean;
  onToggleSelect: (row: TableProps["data"][number], checked: boolean) => void;
  onRowClick: (row: TableProps["data"][number]) => void;
};

const Row = ({ index, style, data }: ListChildComponentProps<RowData>) => {
  const {
    dataState,
    cols,
    colWidth,
    hasActions,
    rowBuild,
    hasModal,
    tableId,
    selected,
    showActions,
    onToggleSelect,
    onRowClick,
  } = data;
  const r = dataState[index];
  const dt = rowBuild(r, index);
  if (hasActions && !dt.choose) {
    const isSelected = selected.findIndex((f) => f.id === r.id) !== -1;
    dt.choose = (
      <DSCheckBox
        // remount when selection flips so the uncontrolled input re-reads
        // defaultChecked (rows used to remount on every parent render)
        key={"check" + r.id?.toString() + isSelected}
        size="small"
        sx={{ p: 0 }}
        disabled={showActions}
        id={"check" + r.id?.toString()}
        defaultChecked={isSelected}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          onToggleSelect(r, e.target.checked);
        }}
      />
    );
  }

  return (
    <DSBox
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        // height: "100%",
      }}
      sx={{
        borderBottom: "1px solid",
        borderColor: "divider",
        cursor: hasModal ? "pointer" : "default",
        "&:hover": { bgcolor: "white.5" },
      }}
      key={index}
      onClick={() => {
        if (hasModal && localStorage.getItem("tableId") !== tableId) {
          onRowClick(r);
          localStorage.removeItem("tableId");
        }
      }}
    >
      {hasActions && (
        <DSBox
          sx={{ display: "inline-block", width: 32, textAlign: "center" }}
          key={index}
        >
          <>{dt.choose}</>
        </DSBox>
      )}
      {cols.map((col: colType, colIndex: number) => (
        <DSBox
          style={{
            display: "flex",
            justifyContent: "center",
            width: col.size ? col.size : colWidth + "px",
            ...(col.scroll ? { overflowY: "scroll", height: "100%" } : {}),
          }}
          key={colIndex}
        >
          <>{dt[col.field]}</>
        </DSBox>
      ))}
    </DSBox>
  );
};

//Deprecated, use DSBaseTable from system-design
function MsTable(props: TableProps) {
  const id = props.id;
  const [selected, setSelected] = useState<Array<any>>([]);
  const [localField, setLocalField] = useState<string>(
    localStorage.getItem(id) || props.initSort || "",
  );
  const [localSearch, setLocalSearch] = useState<string>(props.param || "");
  const [reversed, setReversed] = useState<boolean>(
    localStorage.getItem(id + "Reverse")
      ? localStorage.getItem(id + "Reverse") === "true"
      : false,
  );
  const [dataState, setDataState] = useState<Array<any>>(props.data);
  const [showModal, setShowModal] = useState(false);
  const [chosenRow, setChosenRow] = useState<colType | undefined>(undefined);
  const [showActions, setShowActions] = useState(false);
  const [checkingAll, setCheckingAll] = useState(false);
  const [partialCount, setPartialCount] = useState("");
  const [size, setSize] = useState({ width: 0, height: 0, colWidth: 150 });
  const ref = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { spacing } = useTheme();
  // Matches the Admin layout: app bar + the content area's vertical padding.
  const viewportTop = `${spacing(APPBAR_HEIGHT)} + ${spacing(3)}`;

  useEffect(() => {
    const el = rootRef.current;
    if (!props.fillViewport || !el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY <= 0) return;
      const stickTop = parseFloat(spacing(APPBAR_HEIGHT + 3));
      const distance = el.getBoundingClientRect().top - stickTop;
      if (distance <= 1) return;
      e.preventDefault();
      window.scrollBy({ top: Math.min(e.deltaY, distance) });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [props.fillViewport, spacing]);

  useEffect(() => {
    if (props.param !== undefined) {
      setLocalSearch(props.param);
    }
  }, [props.param]);

  function handleResize() {
    if (ref.current != null) {
      const xy = ref.current.getBoundingClientRect();
      let width = xy.width;
      const cols =
        props.cols.reduce((a, b) => a + (b.size ? b.size : 150), 0) + 10;
      const scroll = width < cols;
      let colWidth = 150;
      if (cols < width) {
        const extra = width - cols;
        colWidth += extra / props.cols.filter((s) => !s.size).length;
      } else {
        width = cols;
      }
      const height = xy.height - 1 - (scroll ? 16 : 0);
      setSize({ width: width, height: height, colWidth: colWidth });
    }
  }

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);

    localStorage.setItem(id, localField);
    localStorage.setItem(id + "Reverse", reversed ? "true" : "false");

    let localData: Array<any> = props.data;
    if (localSearch && !props.shouldHideSearchBar && props.search) {
      localData = localData.filter((i: any) => props.search!(i, localSearch));
    }
    if (localField && !props.cancelSort) {
      localData = localData.sort((a: any, b: any) => {
        let aa = a[localField];
        let bb = b[localField];
        if (localField === "reviewDate") {
          aa = a.review.date;
          bb = b.review.date;
        }
        if (localField === "location") {
          aa = a.location?.value?.country + a.location?.value?.city;
          bb = b.location?.value?.country + b.location?.value?.city;
        }
        const aType = typeof aa;
        const bType = typeof bb;
        if (
          (aa === null || aType === "undefined") &&
          (bb === null || bType === "undefined")
        )
          return 0;
        else if (aa === null || aType === "undefined") return 1;
        else if (bb === null || bType === "undefined") return -1;
        else {
          if (aType === "number" && bType === "number") return bb - aa;
          else if (aType === "string" && bType === "string") {
            return aa.localeCompare(bb);
          }
          if (aa && bb) {
            if (aType === "object") {
              if (localField === "favorites") {
                const laa = aa ? aa.length : 0;
                const lbb = bb ? bb.length : 0;
                return laa - lbb;
              } else if (aa.__proto__ === Date.prototype) {
                return (bb as Date).getTime() - (aa as Date).getTime();
              }
            }
          } else if (!aa) return 1;
          return -1;
        }
      });
      if (reversed) localData = localData.reverse();
      if (props.onSearchChanged && localData.length > 0)
        props.onSearchChanged(localSearch);
    }
    setDataState([...localData]);
    return () => window.removeEventListener("resize", handleResize);
  }, [reversed, localField, localSearch, props.data, props.param]);

  const itemData = useMemo<RowData>(
    () => ({
      dataState,
      cols: props.cols,
      colWidth: size.colWidth,
      hasActions: !!props.actions,
      rowBuild: props.rowBuild,
      hasModal: !!props.modal,
      tableId: id,
      selected,
      showActions,
      onToggleSelect: (row, checked) => {
        if (checked) selected.push(row);
        else selected.splice(selected.indexOf(row), 1);
        setSelected([...selected]);
        if (props.onSelectedChanged) {
          props.onSelectedChanged(selected, dataState);
        }
      },
      onRowClick: (row) => {
        setChosenRow(row);
        setShowModal(true);
      },
    }),
    [
      dataState,
      props.cols,
      size.colWidth,
      props.actions,
      props.rowBuild,
      props.modal,
      id,
      selected,
      showActions,
      props.onSelectedChanged,
    ],
  );

  async function handleCheckAll(
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const checked = event.target.checked;

    // When the parent provides onCheckAll, "select all" means every row
    // matching the current filters (fetched without pagination), not just the
    // rows currently loaded. The parent feeds the full set back in as props.data
    // (which becomes dataState), so here we only drive the selection.
    if (props.onCheckAll) {
      if (checked) {
        setCheckingAll(true);
        try {
          const all = await props.onCheckAll();
          setSelected(all);
          if (props.onSelectedChanged) props.onSelectedChanged(all, all);
        } finally {
          setCheckingAll(false);
        }
      } else {
        setSelected([]);
        if (props.onSelectedChanged) props.onSelectedChanged([], dataState);
      }
      return;
    }

    let tmp = [...selected];
    if (checked) {
      for (const d of dataState) {
        if (!tmp.includes(d)) tmp.push(d);
      }
    } else {
      tmp = tmp.filter((curr) => !dataState.includes(curr));
    }
    setSelected([...tmp]);
    if (props.onSelectedChanged) props.onSelectedChanged(tmp, dataState);
  }

  // Selects the first N rows of every row matching the current filters.
  async function handleCheckSome(): Promise<void> {
    const count = parseInt(partialCount, 10);
    if (!props.onCheckAll || !(count > 0)) return;
    setCheckingAll(true);
    try {
      const all = await props.onCheckAll();
      const some = all.slice(0, count);
      setSelected(some);
      if (props.onSelectedChanged) props.onSelectedChanged(some, all);
    } finally {
      setCheckingAll(false);
    }
  }

  const actionDisabled = props.actionDisabled && props.actionDisabled(selected);
  return (
    <>
      <DSBox
        ref={rootRef}
        sx={{
          display: "flex",
          flexFlow: "column",
          flex: "1 1 auto",
          width: "100%",
          ...(props.fillViewport
            ? {
                height: `calc(100vh - ${viewportTop} - ${spacing(3)})`,
                flex: "0 0 auto",
              }
            : {}),
        }}
      >
        <DSCard
          sx={{
            margin: 0,
            display: "flex",
            flexFlow: "column",
            flex: "1 1 auto",
            bgcolor: "surface.panel",
            border: "1px solid",
            borderColor: "surface.border",
            borderRadius: "24px",
            boxShadow: "none",
            backgroundImage: "none",
            overflow: "hidden",
          }}
        >
          <DSBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <DSTypography variant="h6">{props.title}</DSTypography>
            {props.actions &&
              props.onCheckAll &&
              props.allowPartialSelect &&
              !showActions && (
                <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DSTextField
                    size="small"
                    type="number"
                    placeholder="Count"
                    value={partialCount}
                    disabled={checkingAll}
                    inputProps={{ min: 1 }}
                    sx={{ width: 110 }}
                    onChange={(e) => setPartialCount(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCheckSome();
                    }}
                  />
                  <DSSecondaryButton
                    size="small"
                    disabled={checkingAll || !(parseInt(partialCount, 10) > 0)}
                    title="Select the first N matching rows"
                    onClick={handleCheckSome}
                  >
                    Select
                  </DSSecondaryButton>
                </DSBox>
              )}
            {props.actions && (selected.length > 0 || props.actionsLabel) && (
              <>
                {!showActions && (
                  <DSPrimaryButton
                    disabled={!!actionDisabled || selected.length === 0}
                    title={
                      selected.length === 0
                        ? "Select devices first"
                        : actionDisabled || "Send commands to selected devices"
                    }
                    sx={{ pointerEvents: "auto" }}
                    startIcon={props.actionsIcon ?? <SendIcon />}
                    onClick={() => {
                      setShowActions(true);
                      setDataState([...selected]);
                      if (props.onAction) props.onAction(selected);
                    }}
                  >
                    {props.actionsLabel
                      ? `${props.actionsLabel} (${selected.length.toLocaleString()})`
                      : selected.length}
                  </DSPrimaryButton>
                )}

                {showActions && (
                  <>
                    {props.actions()}
                    <DSSecondaryButton
                      disabled={!!actionDisabled}
                      title={"Exit actions and continue selecting"}
                      size="small"
                      sx={{
                        marginBottom: "5px",
                        fontSize: "80%",
                        height: "fit-content",
                        display: "flex",
                        alignItems: "center",
                      }}
                      onClick={() => {
                        setShowActions(false);
                        setSelected([]);
                        if (!props.shouldHideSearchBar) {
                          setDataState([
                            ...props.data.filter(
                              (i: any) =>
                                props.search && props.search(i, localSearch),
                            ),
                          ]);
                        }
                      }}
                    >
                      Exit
                    </DSSecondaryButton>
                  </>
                )}
              </>
            )}
            {props.buttons && <>{props.buttons}</>}
          </DSBox>
          <DSBox
            sx={{
              padding: 0,
              flex: "1 1 auto",
              display: "flex",
              flexFlow: "column",
              overflow: "auto",
              overflowX: "auto",
            }}
          >
            {!props.shouldHideSearchBar && (
              <DSTextField
                fullWidth
                size="small"
                placeholder="Search"
                disabled={showActions}
                defaultValue={localSearch}
                sx={{ px: 2, pb: 1.5 }}
                type="text"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  clearTimeout(timeout);
                  timeout = setTimeout(() => {
                    setLocalSearch(e.target.value.toLowerCase());
                  }, 100);
                }}
              />
            )}
            <DSBox
              sx={{
                display: "flex",
                alignItems: "center",
                width: size.width,
                py: 0.5,
                backgroundColor: "white.5",
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderColor: "surface.border",
              }}
            >
              {props.actions && (
                <DSFormControlLabel
                  sx={{ width: 32, m: 0, textAlign: "center" }}
                  labelPlacement="top"
                  label={<DSTypography variant="caption">All</DSTypography>}
                  control={
                    checkingAll ? (
                      <DSCircularProgress size={16} sx={{ p: 0 }} />
                    ) : (
                      <DSCheckBox
                        size="small"
                        sx={{ p: 0 }}
                        disabled={dataState.length == 0 || showActions}
                        checked={
                          dataState.length > 0 &&
                          dataState.every((curr) =>
                            selected.some((s) => s.id === curr.id),
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleCheckAll(e)}
                      />
                    )
                  }
                />
              )}
              {props.cols.map((col: colType, index: number) => (
                <DSBox
                  sx={{
                    width: col.size ? col.size : size.colWidth + "px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                  key={index}
                >
                  {col.noHeadline || !col.label ? (
                    ""
                  ) : (
                    <DSButton
                      variant="text"
                      color="inherit"
                      disableRipple={!col.sort}
                      sx={{
                        textTransform: "uppercase",
                        fontSize: 12,
                        color: "text.secondary",
                        boxShadow: "none",
                        minHeight: 0,
                        minWidth: 0,
                        px: 1.25,
                        py: 1,
                        cursor: col.sort ? "pointer" : "default",
                        "&:hover": {
                          boxShadow: "none",
                          backgroundColor: col.sort ? "white.8" : "transparent",
                        },
                      }}
                      onClick={
                        col.sort
                          ? () => {
                              props.onSortClick && props.onSortClick(col.field);
                              if (col.field !== localField) {
                                setReversed(false);
                                setLocalField(col.field);
                              } else setReversed(!reversed);
                            }
                          : undefined
                      }
                    >
                      <DSBox
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "nowrap",
                        }}
                      >
                        {col.label}
                        {col.sort && (
                          <DSBox
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              ml: 0.5,
                            }}
                          >
                            {col.field !== localField || reversed ? (
                              <KeyboardArrowUpIcon
                                sx={{ fontSize: 14, display: "grid" }}
                              />
                            ) : (
                              ""
                            )}
                            {col.field !== localField || !reversed ? (
                              <KeyboardArrowDownIcon
                                sx={{ fontSize: 14, display: "grid" }}
                              />
                            ) : (
                              ""
                            )}
                          </DSBox>
                        )}
                      </DSBox>
                    </DSButton>
                  )}
                </DSBox>
              ))}
            </DSBox>
            <div ref={ref} style={{ flex: "1 1 auto", position: "relative" }}>
              <List
                onItemsRendered={(o) => {
                  if (
                    props.onScrollDown &&
                    o.overscanStopIndex === dataState.length - 1
                  ) {
                    props.onScrollDown();
                  }
                }}
                itemCount={dataState.length}
                itemSize={props.rowHeight ?? 105}
                height={size.height}
                width={size.width}
                itemData={itemData}
              >
                {Row}
              </List>
              {checkingAll && (
                <DSBox
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    bgcolor: "rgba(0,0,0,0.6)",
                    zIndex: 2,
                  }}
                >
                  <DSCircularProgress />
                  <DSTypography variant="body2">
                    Fetching all matching devices…
                  </DSTypography>
                </DSBox>
              )}
              <DSTypography
                variant="caption"
                sx={{ position: "absolute", bottom: 0, right: 0 }}
              >
                rows: {dataState.length}
              </DSTypography>
            </div>
          </DSBox>
        </DSCard>
      </DSBox>
      {props.modal && (
        <DSDialog
          fullWidth
          maxWidth="lg"
          open={showModal}
          onClose={() => setShowModal(false)}
        >
          <props.modal
            setShowModal={setShowModal}
            row={chosenRow}
            fixedData={props.rowBuild}
            data={props.data}
            handleChanged={props.handleChangedKlydo}
          />
        </DSDialog>
      )}
    </>
  );
}

export default MsTable;
